<template>
  <div>
    <div
      v-if="chatSession"
      class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5"
    >
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'AI Chats', to: ROUTES.ADMIN.CHATS.CHAT_LIST },
            {
              title: chatSession.uuid,
              to: ROUTES.ADMIN.CHATS.CHAT_DETAIL.replace(':uuid', chatSession.uuid)
            }
          ]"
        />
      </div>
      <!-- End Page Header -->

      <!-- Chat Session Detail Card -->
      <div class="flex flex-col space-y-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <!-- Header Section -->
        <div
          class="flex flex-col items-start justify-between gap-4 border-b border-gray-200 pb-5 md:flex-row"
        >
          <div>
            <h2 class="text-xl font-semibold text-gray-800">
              {{ chatSession.title || 'AI Chat Session' }}
            </h2>
            <p class="mt-1 text-sm text-gray-500">
              Created on {{ formatDate(chatSession.created_at) }}
            </p>
          </div>
          <div class="flex items-center gap-3">
            <span
              v-if="chatSession.session_state"
              class="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium"
              :class="{
                'bg-green-100 text-green-800':
                  chatSession.session_state === SessionStateEnum.COMPLETED,
                'bg-blue-100 text-blue-800':
                  chatSession.session_state === SessionStateEnum.IN_PROGRESS,
                'bg-gray-100 text-gray-800': chatSession.session_state === SessionStateEnum.CREATED
              }"
            >
              {{ getSessionStateDisplay(chatSession.session_state) }}
            </span>
          </div>
        </div>

        <!-- Ratings Section - Improved Design -->
        <div class="mt-2">
          <h3 class="mb-4 text-lg font-medium text-gray-800">Ratings & Feedback</h3>

          <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
            <!-- System Rating Card -->
            <div class="rounded-lg border border-purple-200 bg-purple-50 p-4 shadow-sm">
              <div class="mb-2 flex items-center justify-between">
                <h4 class="text-md font-medium text-purple-800">System Rating</h4>
                <div class="flex items-center">
                  <span class="text-xl font-bold text-purple-700">
                    {{ chatSession.system_rating || 'N/A' }}
                  </span>
                  <span class="ml-1 text-sm text-purple-600">/5</span>
                </div>
              </div>

              <div
                v-if="chatSession.system_rating_explanation"
                class="mt-3"
              >
                <h5 class="mb-1 text-sm font-medium text-purple-800">Explanation:</h5>
                <div
                  class="prose prose-sm max-h-48 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-3"
                  v-html="renderMarkdown(chatSession.system_rating_explanation)"
                ></div>
              </div>

              <div
                v-if="chatSession.system_rating_improvement_suggestions"
                class="mt-3"
              >
                <h5 class="mb-1 text-sm font-medium text-purple-800">Improvement Suggestions:</h5>
                <div
                  class="prose prose-sm max-h-48 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-3"
                  v-html="renderMarkdown(chatSession.system_rating_improvement_suggestions)"
                ></div>
              </div>
            </div>

            <!-- Human & User Info Card -->
            <div class="rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
              <div class="flex h-full flex-col justify-between">
                <!-- Human Rating Section -->
                <div class="mb-4">
                  <div class="mb-2 flex items-center justify-between">
                    <h4 class="text-md font-medium text-blue-800">Human Rating</h4>
                    <div
                      v-if="chatSession.human_rating"
                      class="flex items-center"
                    >
                      <span class="text-xl font-bold text-blue-700">
                        {{ chatSession.human_rating }}
                      </span>
                      <span class="ml-1 text-sm text-blue-600">/5</span>
                    </div>
                  </div>

                  <!-- Human Rating Display if already rated -->
                  <div
                    v-if="chatSession.human_rating"
                    class="py-2"
                  >
                    <div class="flex gap-1">
                      <svg
                        v-for="star in 5"
                        :key="star"
                        class="h-5 w-5"
                        :class="
                          Number(chatSession.human_rating) >= star
                            ? 'text-yellow-500'
                            : 'text-gray-300'
                        "
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                    <p class="mt-1 text-sm text-blue-700">
                      This chat session has already been rated.
                    </p>
                  </div>

                  <!-- Human Rating Form if not yet rated -->
                  <div
                    v-else
                    class="py-2"
                  >
                    <p class="mb-2 text-sm text-blue-700">Rate this chat session:</p>

                    <div class="flex items-center gap-1">
                      <button
                        v-for="star in 5"
                        :key="star"
                        class="focus:outline-none"
                        :disabled="isSubmitting"
                        @click="selectRating(star)"
                      >
                        <svg
                          class="h-8 w-8 transition-colors duration-150"
                          :class="[
                            hoveredRating >= star || selectedRating >= star
                              ? 'text-yellow-500'
                              : 'text-gray-300',
                            isSubmitting
                              ? 'cursor-not-allowed opacity-50'
                              : 'cursor-pointer hover:scale-110'
                          ]"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          @mouseenter="hoveredRating = star"
                          @mouseleave="hoveredRating = 0"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>

                    <div class="mt-3 flex gap-2">
                      <button
                        class="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                        :disabled="!selectedRating || isSubmitting"
                        :class="{
                          'cursor-not-allowed opacity-50': !selectedRating || isSubmitting
                        }"
                        @click="submitRating"
                      >
                        {{ isSubmitting ? 'Submitting...' : 'Submit Rating' }}
                      </button>

                      <button
                        v-if="selectedRating"
                        class="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors
                          hover:bg-gray-300"
                        :disabled="isSubmitting"
                        :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
                        @click="selectedRating = 0"
                      >
                        Cancel
                      </button>
                    </div>

                    <p
                      v-if="ratingError"
                      class="mt-2 text-sm text-red-600"
                    >
                      {{ ratingError }}
                    </p>
                  </div>
                </div>

                <!-- User Info Section -->
                <div class="border-t border-blue-200 pt-3">
                  <h4 class="text-md mb-2 font-medium text-blue-800">Session Info</h4>
                  <div class="flex flex-col gap-2">
                    <div class="flex justify-between">
                      <span class="text-sm text-blue-800">Started By:</span>
                      <span class="text-sm text-gray-700">{{ chatSession.started_by_user }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-sm text-blue-800">Messages:</span>
                      <span class="text-sm text-gray-700">{{ chatSession.messages.length }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chat Messages -->
        <div class="mt-6 border-t border-gray-200 pt-5">
          <h3 class="mb-3 text-lg font-medium text-gray-700">Chat Messages</h3>
          <div class="space-y-3">
            <AdminChatMessage
              v-for="(msg, index) in chatSession.messages"
              :key="index"
              :message="msg"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-else
      class="flex h-64 items-center justify-center"
    >
      <div class="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { AiChatSessionsService } from '@/types/generated/services.gen'
  import {
    type AIChatSession,
    type AIChatSessionMessage,
    AIChatSessionMessageRoleEnum,
    SessionStateEnum
  } from '@/types/generated/types.gen'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import MarkdownIt from 'markdown-it'
  import AdminChatMessage from '@/components/admin/chats/AdminChatMessage.vue'

  const route = useRoute()
  const params = route.params as { uuid: string }
  const md = new MarkdownIt({
    html: true,
    breaks: true,
    linkify: true
  })

  const chatSession = ref<AIChatSession | null>(null)
  const isLoading = ref(false)
  const selectedRating = ref(0)
  const hoveredRating = ref(0)
  const isSubmitting = ref(false)
  const ratingError = ref('')

  onMounted(async () => {
    isLoading.value = true
    try {
      chatSession.value = await AiChatSessionsService.aiChatSessionsRetrieve({
        uuid: params.uuid
      })
    } catch (error) {
      console.error('Error fetching chat session:', error)
    } finally {
      isLoading.value = false
    }
  })

  const getSessionStateDisplay = (sessionState: string) => {
    switch (sessionState) {
      case SessionStateEnum.COMPLETED:
        return 'Completed'
      case SessionStateEnum.IN_PROGRESS:
        return 'In Progress'
      case SessionStateEnum.CREATED:
        return 'Created'
      default:
        return sessionState
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''

    const date = new Date(dateString)
    return new Intl.DateTimeFormat('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const renderMarkdown = (content: string) => {
    if (!content) return ''
    try {
      return md.render(content)
    } catch (error) {
      console.error('Error rendering markdown:', error)
      return content // Return the raw content if there's an error
    }
  }

  const selectRating = (rating: number) => {
    selectedRating.value = rating
  }

  const submitRating = async () => {
    if (!chatSession.value || !selectedRating.value) return

    isSubmitting.value = true
    ratingError.value = ''

    try {
      // Create a copy of the chat session with the new human rating
      const updatedChatSession: AIChatSession = {
        ...chatSession.value,
        human_rating: selectedRating.value.toString()
      }

      // Update the chat session with the new rating
      const response = await AiChatSessionsService.aiChatSessionsPartialUpdate({
        uuid: chatSession.value.uuid,
        requestBody: {
          human_rating: selectedRating.value.toString()
        }
      })

      // Update the local chat session with the response
      chatSession.value = response

      // Reset the form
      selectedRating.value = 0
      hoveredRating.value = 0
    } catch (error) {
      console.error('Error updating chat session rating:', error)
      ratingError.value = 'Failed to submit rating. Please try again.'
    } finally {
      isSubmitting.value = false
    }
  }
</script>

<style scoped>
  /* Keep only the CSS needed for markdown in rating explanations and suggestions */
  :deep(.markdown-content h1),
  :deep(.markdown-content h2),
  :deep(.markdown-content h3),
  :deep(.markdown-content h4),
  :deep(.markdown-content h5),
  :deep(.markdown-content h6) {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }

  :deep(.markdown-content p) {
    margin-bottom: 0.75rem;
  }

  :deep(.markdown-content ul),
  :deep(.markdown-content ol) {
    margin-left: 1.5rem;
    margin-bottom: 0.75rem;
  }

  :deep(.markdown-content li) {
    margin-bottom: 0.25rem;
  }

  :deep(.markdown-content a) {
    color: #3b82f6;
    text-decoration: underline;
  }

  :deep(.markdown-content code) {
    background-color: #f1f5f9;
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    font-family: monospace;
  }

  :deep(.markdown-content pre) {
    background-color: #f1f5f9;
    padding: 1rem;
    border-radius: 0.375rem;
    overflow-x: auto;
    margin: 0.75rem 0;
  }

  :deep(.markdown-content pre code) {
    background-color: transparent;
    padding: 0;
  }

  :deep(.markdown-content blockquote) {
    border-left: 4px solid #e5e7eb;
    padding-left: 1rem;
    font-style: italic;
    margin: 0.75rem 0;
  }
</style>
